/**
 * Priority List Types
 * 
 * A Priority List represents increasing levels of organizational commitment
 * to a set of priorities. It progresses through governance states:
 * 
 * Proposed → Decided → Ratified → Published
 * 
 * - Proposed: Output of A (end of Catchball loop)
 * - Decided: Approved-in-principle; planning authorized (green light)
 * - Ratified: Final locked set; overrides tracked
 * - Published: Communicated Plan of Record
 * 
 * ============================================================================
 * STATE MACHINE INVARIANTS (Doc 02, Doc 03, Doc 05)
 * ============================================================================
 * 
 * INVARIANT 1: Governance state overlays but does not block core transitions.
 *   - Governance is orthogonal to Cycle phase progression
 *   - A Cycle can advance phases even if governance is pending
 *   - Governance affects visibility/authority, not phase eligibility
 * 
 * INVARIANT 2: Decided ≠ Ratified.
 *   - Decided = "Green light for planning" (approval-in-principle)
 *   - Ratified = "Locked and final" (overrides are tracked)
 *   - These are distinct commitment levels, not synonyms
 * 
 * INVARIANT 3: Accept (Provisional) maps to Decided.
 *   - When executive accepts a Catchball proposal provisionally,
 *     the result is a Priority List in "decided" state
 *   - Ratification requires a separate, explicit action
 * 
 * INVARIANT 4: Governance does not advance cycle phases.
 *   - Moving from Proposed → Decided does NOT trigger phase transition
 *   - Governance completion informs but does not control DEGAP phases
 */

// ============================================================================
// Core Types
// ============================================================================

export type PrioritySetId = string;

/**
 * Priority List states - governance progression
 */
export type PrioritySetState = 
  | 'proposed'    // Output of A (end of Catchball loop)
  | 'decided'     // Approved-in-principle, planning green-lit
  | 'ratified'    // Final locked set, overrides tracked
  | 'published'   // Plan of Record
  | 'superseded'; // Replaced by newer set

/**
 * State order for governance flow
 */
export const PRIORITY_SET_STATE_ORDER: PrioritySetState[] = [
  'proposed',
  'decided',
  'ratified',
  'published',
];

// ============================================================================
// Override Types
// ============================================================================

/**
 * Types of overrides that can occur between governance states
 */
export type OverrideType = 
  | 'promote'     // Item moved up in rank
  | 'demote'      // Item moved down in rank
  | 'add'         // New item added (post-A)
  | 'remove'      // Item removed
  | 'modify'      // Scope/definition changed
  | 're_bucket'   // Moved between focus lists/themes
  | 'tie_break';  // Resolved tie between near-equals

/**
 * A tracked override to the priority list
 */
export interface PriorityOverride {
  id: string;
  
  /** Type of override */
  type: OverrideType;
  
  /** Item being overridden */
  itemId: string;
  
  /** Previous state (if applicable) */
  previousRank?: number;
  previousFocusListId?: string;
  
  /** New state */
  newRank?: number;
  newFocusListId?: string;
  
  /** Required rationale */
  rationale: string;
  
  /** Who made the override */
  overriddenBy: string;
  overriddenAt: Date;
  
  /** At which governance transition this occurred */
  duringTransition: 'proposed_to_decided' | 'decided_to_ratified';
}

// ============================================================================
// Governance Events
// ============================================================================

/**
 * Types of governance events
 */
export type GovernanceEventType = 
  | 'created'
  | 'decided'
  | 'ratified'
  | 'published'
  | 'superseded'
  | 'override_added'
  | 'reopened';

/**
 * A governance event in the audit trail.
 *
 * `type` admits raw strings because the `governance_events` table dropped its
 * event_type CHECK (migration 069): rows written by RPCs and other subsystems
 * (e.g. STAKEHOLDER_PULSE_GENERATED) can carry types outside the canonical
 * union. Known DB values are normalized to GovernanceEventType on load.
 */
export interface GovernanceEvent {
  id: string;
  prioritySetId: PrioritySetId;

  /** Event type */
  type: GovernanceEventType | (string & {});
  
  /** Actor */
  actorId: string;
  actorName?: string;
  
  /** Timestamp */
  occurredAt: Date;
  
  /** Optional note/rationale */
  note?: string;
  
  /** State before (for transitions) */
  fromState?: PrioritySetState;
  
  /** State after (for transitions) */
  toState?: PrioritySetState;
  
  /** Related override (for override_added events) */
  overrideId?: string;
}

// ============================================================================
// Decision Record
// ============================================================================

/**
 * Decision Record - captures the Proposed → Decided transition
 * This is the "green light for planning" checkpoint
 */
export interface DecisionRecord {
  /** Approval authority (who decided) */
  decidedBy: string;
  decidedByName?: string;
  
  /** When the decision was made */
  decidedAt: Date;
  
  /** Notes or conditions */
  note?: string;
  
  /** Conditions attached to the decision */
  conditions?: string[];
  
  /** Confidence level */
  confidence?: 'high' | 'medium' | 'low';
}

// ============================================================================
// Ratification Record
// ============================================================================

/**
 * Ratification Record - captures the Decided → Ratified transition
 * This is the final lock with full provenance
 */
export interface RatificationRecord {
  /** Who ratified */
  ratifiedBy: string;
  ratifiedByName?: string;
  
  /** When ratified */
  ratifiedAt: Date;
  
  /** Final note */
  note?: string;

  /** All overrides relative to Proposed state */
  overrides: PriorityOverride[];

  /**
   * Snapshot of the final ranked item IDs.
   * Optional: the DB has no dedicated column for this snapshot — consumers
   * fall back to the set's own rankedItemIds when absent.
   */
  finalRankedItemIds?: string[];

  /** Cut line position */
  cutLinePosition?: number;
}

// ============================================================================
// Publication Record
// ============================================================================

/**
 * Publication Record - captures the Ratified → Published transition
 * Creates the Plan of Record
 */
export interface PublicationRecord {
  /** Who published */
  publishedBy: string;
  publishedByName?: string;
  
  /** When published */
  publishedAt: Date;
  
  /** Publication message/announcement */
  message?: string;
  
  /** Distribution list (who was notified) */
  notifiedUserIds?: string[];
  
  /** External publication (e.g., link to announcement) */
  externalUrl?: string;
}

// ============================================================================
// Main Interface
// ============================================================================

/**
 * A Priority List - the governance artifact for priorities.
 *
 * Mirrors the `priority_sets` table 1:1 (see supabase live schema): the
 * decision/ratification/publication records map to the decided_*, ratified_*,
 * and published_* column groups, and `events` is loaded from `governance_events`
 * rows carrying this set's id.
 */
export interface PrioritySet {
  id: PrioritySetId;

  /** Parent cycle */
  cycleId: string;

  /** Track this set belongs to (priority lists are track-scoped) */
  trackId: string;

  /**
   * Source loop that produced this set. NULL when the set was published
   * directly from a session outside a cascade (migration 705).
   */
  loopId: string | null;

  /**
   * Session whose results produced this set. NULL for sets synthesized from a
   * catchball proposal packet, which reach the record through the loop instead.
   */
  sessionId?: string | null;

  /** Current governance state */
  state: PrioritySetState;

  /** Name for display */
  name: string;

  /** Description */
  description?: string;

  // ================================
  // Content
  // ================================

  /** Item Pools (née Focus Lists) included in this set */
  itemPoolIds: string[];

  /** All item IDs (denormalized for quick access) */
  itemIds: string[];

  /** Ranked order of items (from final output) */
  rankedItemIds: string[];

  /** Cut line position */
  cutLinePosition?: number;

  // ================================
  // Governance Records
  // ================================

  /** When proposed (from loop output_ready) */
  proposedAt: Date;
  proposedBy?: string;
  
  /** Decision record (Proposed → Decided) */
  decision?: DecisionRecord;
  
  /** Ratification record (Decided → Ratified) */
  ratification?: RatificationRecord;
  
  /** Publication record (Ratified → Published) */
  publication?: PublicationRecord;
  
  // ================================
  // Audit
  // ================================
  
  /** Full event history */
  events: GovernanceEvent[];
  
  // ================================
  // Timestamps
  // ================================
  
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// State Transitions
// ============================================================================

/**
 * Allowed state transitions for Priority Lists
 */
export const PRIORITY_SET_TRANSITIONS: Record<PrioritySetState, PrioritySetState[]> = {
  'proposed': ['decided', 'superseded'],
  'decided': ['ratified', 'proposed', 'superseded'], // Can revert to proposed if issues found
  'ratified': ['published', 'superseded'],
  'published': ['superseded'],
  'superseded': [],
};

/**
 * Check if a state transition is allowed
 */
export function canTransitionPrioritySet(
  from: PrioritySetState, 
  to: PrioritySetState
): boolean {
  return PRIORITY_SET_TRANSITIONS[from].includes(to);
}

// ============================================================================
// Display Helpers
// ============================================================================

/**
 * Human-readable labels for Priority List states
 */
export const PRIORITY_SET_STATE_LABELS: Record<PrioritySetState, string> = {
  'proposed': 'Proposed',
  'decided': 'Decided (Planning Authorized)',
  'ratified': 'Ratified (Final)',
  'published': 'Published (Plan of Record)',
  'superseded': 'Superseded',
};

/**
 * State badge colors
 */
export const PRIORITY_SET_STATE_COLORS: Record<PrioritySetState, string> = {
  'proposed': 'bg-amber-100 text-amber-800',
  'decided': 'bg-blue-100 text-blue-800',
  'ratified': 'bg-green-100 text-green-800',
  'published': 'bg-purple-100 text-purple-800',
  'superseded': 'bg-gray-100 text-gray-500',
};

/**
 * Get progress percentage for Priority List state
 */
export function getPrioritySetProgress(state: PrioritySetState): number {
  const index = PRIORITY_SET_STATE_ORDER.indexOf(state);
  if (index === -1) return 0;
  return Math.round(((index + 1) / PRIORITY_SET_STATE_ORDER.length) * 100);
}

// ============================================================================
// Planning Enablement
// ============================================================================

/**
 * Planning enablement status based on Priority List state
 */
export type PlanningEnablement = 
  | 'not_authorized'    // Proposed state - planning is premature
  | 'authorized'        // Decided state - planning can begin
  | 'locked';           // Ratified/Published - planning baseline is locked

/**
 * Get planning enablement from Priority List state
 */
export function getPlanningEnablement(state: PrioritySetState): PlanningEnablement {
  switch (state) {
    case 'proposed':
      return 'not_authorized';
    case 'decided':
      return 'authorized';
    case 'ratified':
    case 'published':
      return 'locked';
    case 'superseded':
      return 'not_authorized';
  }
}

export const PLANNING_ENABLEMENT_LABELS: Record<PlanningEnablement, string> = {
  'not_authorized': 'Planning Not Authorized',
  'authorized': 'Planning Authorized',
  'locked': 'Planning Baseline Locked',
};
